import { useMemo, useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Star, Leaf, Clock, ArrowRight, UtensilsCrossed, Truck } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const MaskedLine = ({ children, index, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1, delay: 0.25 + index * 0.16, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const markY = useTransform(scrollYProgress, [0, 1], [0, -90]);

  const particles = useMemo(
    () =>
      Array.from({ length: 14 }, (_, i) => ({
        left: `${(i * 37 + 13) % 100}%`,
        top: `${(i * 53 + 7) % 100}%`,
        size: 3 + (i % 3),
        delay: (i % 7) * 0.6,
        duration: 5 + (i % 4),
      })),
    []
  );

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-[106px]"
    >
      <div className="pointer-events-none absolute -left-40 top-1/4 h-[480px] w-[480px] rounded-full bg-[#F57C00]/10 blur-[140px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[420px] w-[420px] rounded-full bg-[#388E3C]/10 blur-[140px]" />

      {particles.map((p, i) => (
        <motion.span
          key={i}
          className="pointer-events-none absolute rounded-full bg-[#F57C00]/50"
          style={{ left: p.left, top: p.top, width: p.size, height: p.size }}
          animate={{ y: [0, -26, 0], opacity: [0.15, 0.7, 0.15] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-14 px-5 py-16 lg:grid-cols-12 lg:px-8">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-7 flex flex-wrap items-center gap-3"
          >
            <span className="rounded-full border border-[#F57C00]/30 bg-[#F57C00]/10 px-4 py-1.5 font-mono-accent text-[11px] uppercase tracking-[0.25em] text-[#FF9800]">
              Pure Veg • South Indian
            </span>
            <span className="rounded-full border border-white/10 px-4 py-1.5 font-mono-accent text-[11px] uppercase tracking-[0.25em] text-zinc-400">
              Sohna City, Gurgaon
            </span>
          </motion.div>

          <h1
            data-testid="hero-heading"
            className="font-display text-4xl font-semibold leading-[1.06] tracking-tight sm:text-5xl lg:text-[5.2rem]"
          >
            <MaskedLine index={0}>Taste the</MaskedLine>
            <MaskedLine index={1} className="text-gradient-saffron italic">
              Tradition,
            </MaskedLine>
            <MaskedLine index={2}>
              served <span className="text-gradient-leaf">pure.</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
          >
            Hand-ground gunpowder podi, ghee-kissed dosas and slow-simmered
            sambar — authentic South Indian soul food, now at Global City
            Center, Sector 33, Sohna.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-explore-menu-btn"
              onClick={() => scrollToSection("#menu")}
              className="group flex items-center gap-2.5 rounded-full bg-[#F57C00] px-7 py-3.5 font-grotesk text-sm font-semibold text-black transition-all duration-300 hover:bg-[#FF9800] hover:shadow-[0_0_36px_rgba(245,124,0,0.5)]"
            >
              Explore the Menu
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-book-table-btn"
              onClick={() => scrollToSection("#reserve")}
              className="flex items-center gap-2.5 rounded-full border border-white/15 px-7 py-3.5 font-grotesk text-sm font-semibold text-zinc-100 transition-all duration-300 hover:border-[#F57C00]/60 hover:text-[#FF9800]"
            >
              <UtensilsCrossed size={16} />
              Reserve a Table
            </button>
          </motion.div>

          <motion.div
            data-testid="hero-badges"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3"
          >
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <Star size={15} className="fill-[#D4AF37] text-[#D4AF37]" />
              <span className="font-mono-accent font-semibold text-[#F5F2EB]">4.0</span>
              <span className="text-zinc-500">on Swiggy</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <Leaf size={15} className="text-[#4CAF50]" />
              100% Pure Vegetarian
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <Clock size={15} className="text-[#FF9800]" />
              8:00 AM – 10:30 PM
            </div>
            <div className="flex items-center gap-2 text-sm text-zinc-300">
              <Truck size={15} className="text-[#4CAF50]" />
              Free delivery up to 3 KM • Min ₹300
            </div>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.92, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="absolute -inset-6 rounded-[2.5rem] bg-[#F57C00]/15 blur-3xl" />
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden rounded-[2rem] border border-white/10 shadow-[0_30px_80px_rgba(0,0,0,0.7)]"
            >
              <img
                src="https://images.unsplash.com/photo-1588644525273-f37b60d78512?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                alt="South Indian thali at Podi Sambaram"
                className="h-[420px] w-full object-cover sm:h-[500px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between">
                <div>
                  <p className="font-mono-accent text-[10px] uppercase tracking-[0.3em] text-[#FF9800]">
                    Signature
                  </p>
                  <p className="font-display text-xl font-semibold text-white">
                    South Indian Thali
                  </p>
                </div>
                <span className="rounded-full bg-black/60 px-4 py-2 font-mono-accent text-sm font-semibold text-[#FFB74D] backdrop-blur">
                  ₹140
                </span>
              </div>
            </motion.div>

            <motion.img
              src="/assets/mark.png"
              alt="Podi Sambaram emblem"
              style={{ y: markY }}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -left-8 -top-8 h-24 w-24 rounded-full border-2 border-[#F57C00]/50 bg-black object-cover shadow-[0_10px_40px_rgba(245,124,0,0.35)] sm:h-28 sm:w-28"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
